import PropTypes from 'prop-types';
import React from 'react';
import moment from 'moment';
import _ from 'lodash';
import styles from './Success.less';
import UpgradeStrip from '../../../../shared/components/UpgradeStrip/UpgradeStrip';
import ReservationIcon from '../../../../shared/components/Svg/ReservationIcon';
import ReservationDetails from '../../../../shared/components/ReservationDetails/ReservationDetails';
import PhoneUtils from '@wix/wixrest-utils/dist/PhoneUtils';
import { PropertiesUtils as OPU } from '@wix/wixrest-utils';
import Properties from '@wix/wixrest-properties';
import ContentConnector from '@wix/restaurants-components-css-modules/dist/shared/I18nProvider/ContentConnector';
import { printTime } from '../../../../shared/utils/ReservationHelpers';
import { isTab } from '../../../../shared/utils/A11yHelpers';
import { MODAL_A11Y_IDS } from '../../../../shared/constants';

export default ContentConnector({prefix: 'success_'})(class extends React.Component {
    static displayName = 'Success';

    static propTypes = {
        reservation: PropTypes.object.isRequired,
        onConfirm: PropTypes.func.isRequired,
        isCustomDesign: PropTypes.bool,
        preview: PropTypes.bool,
        onPremiumClick: PropTypes.func,
        gotoDashboard: PropTypes.func,
        restaurant: PropTypes.object.isRequired,
        onButtonBlur: PropTypes.func,
        doneButtonRef: PropTypes.func
    };

    static defaultProps = {
        onButtonBlur: () => {},
        doneButtonRef: () => {}
    };

    componentDidMount() {
        this.buttonElem.focus();
    }

    render() {
        const { isCustomDesign, onConfirm, preview, content } = this.props;
        const header = this._getHeader();
        const details = this._getDetails();
        const footer = this._getFooter();
        const upgradeStrip = this._getUpgradeStrip();

        return (
            <div className={`${styles.wrapper} ${isCustomDesign ? styles.custom : ''} ${preview ? styles.preview : ''}`}>
                {upgradeStrip}
                <div className={styles.innerWrapper}>
                    {header}
                    {details}
                    <button ref={el => this.buttonElem = el}
                            className={styles.button}
                            onClick={onConfirm}
                            data-hook="success_got_it">
                        <span>{content.get('btn')}</span>
                    </button>
                    {footer}
                </div>
            </div>
        );
    }

    _getUpgradeStrip = () => {
        const { preview, onPremiumClick, gotoDashboard, content } = this.props;
        return preview ? (
                <UpgradeStrip
                    onPremium={onPremiumClick}
                    onSecondaryButton={gotoDashboard}
                    primaryButtonText={content.get('preview_header_upgrade_button')}
                    secondaryButtonText={content.get('preview_header_button')}
                    tip={content.get('preview_header_helptip')}
                    text={content.get('preview_header_text')}/>
            ) : null;
    };

    _getHeader = () => {
        const { reservation, isCustomDesign, content } = this.props;
        const name = reservation.contact.firstName;
        const status = reservation.status;
        let description = content.get('description_confirmed');

        if (status.toLowerCase() !== 'accepted') {
            description = content.get('description_pending');
        }

        return (
            <div className={`${styles.header} ${isCustomDesign ? styles.custom : ''}`}>
                <div className={styles.icon}><ReservationIcon /></div>
                <div className={styles.title} data-hook="success_title" id={MODAL_A11Y_IDS.TITLE_ID}>{content.get('title', {params: {name}})}</div>
                <div className={styles.description} data-hook="success_description" id={MODAL_A11Y_IDS.DESCRIPTION_ID}>{description}</div>
            </div>
        );
    };

    _getDetails = () => {
        const { reservation, isCustomDesign, content, form } = this.props;
        const { date, time, party } = this._getValues();
        const locationName = _.get(form, 'reservation.location.name');
        const customFields = OPU.get(reservation, Properties.Reservations.CUSTOMFIELDS, 'com.wix.restaurants');

        return (
            <div className={styles.details}>
                {locationName ?
                 <div className={styles.generalDetails} data-hook="success_id">
                    {this._getDetailElement(reservation.id, content.get('id_label'), 'success_id')}
                    {this._getDetailElement(locationName, content.get('location') || 'Location', 'success_location', true)}
                </div>
                 : <div className={styles.reservationId} data-hook="success_id">
                    {this._getDetailElement(reservation.id, content.get('id_label'), 'success_id')}
                </div>}
                <ReservationDetails
                    date={date}
                    time={time}
                    party={party}
                    customFields={customFields}
                    isCustomDesign={isCustomDesign}/>
                {reservation.comment ? <div className={styles.request}>
                        {this._getDetailElement(reservation.comment, content.get('comment_label'), 'success_request')}
                    </div> : null}
            </div>
        );
    };

    _getValues = () => {
        const { reservation, restaurant } = this.props;
        const selectedDate = moment.tz(reservation.time, restaurant.timezone);
        return {
            date: selectedDate.format('ddd L'),
            time: printTime(selectedDate),
            party: this._getPartyLabel()
        };
    };

    _getPartyLabel = () => {
        const { reservation, form } = this.props;
        const { options } = form;
        const { partySize } = reservation;
        const partyOptions = options.party;
        let label = null;

        for (let i = 0; i < partyOptions.length; i++) {
            if (_.isEqual(partySize, partyOptions[i].value)) {
                label = partyOptions[i].name;
                break;
            }
        }

        return label;
    };

    _getDetailElement = (detail, label, dataHook, ellipse = false) => {
        return (
            <div className={styles.detailBox} data-hook={dataHook}>
                <div key="detail" className={`${styles.detail} ${ellipse ? styles.ellipseInfo : ''}`}>{detail}</div>
                <div key="label" className={styles.label}>{label}</div>
            </div>
        );
    };


    _getFooter = () => {
        const { reservation, restaurant, content, doneButtonRef } = this.props;
        const restaurantPhone = _.get(restaurant, 'contact.phone', '');
        const countryCode = _.get(restaurant, 'address.countryCode', '');
        const displayPhone = PhoneUtils.displayPhone(restaurantPhone, countryCode);
        const phoneNumber = PhoneUtils.internationalizePhone(restaurantPhone, countryCode);
        const delimiter = '@@@@';
        const text = content.get('footer', {params: {time: printTime(moment(reservation.created)), email: reservation.contact.email, phone: delimiter}});
        const textArr = text.split(delimiter);

        return (
            <div className={styles.footer} data-hook="success_footer">
                {textArr[0]}
                <a href={`tel:${phoneNumber}`}
                   ref={doneButtonRef}
                   onKeyDown={this._onButtonKeyDown}>{displayPhone}</a>
                {textArr[1]}
            </div>
        );
    };

    _onButtonKeyDown = (e) => {
        const { onButtonBlur } = this.props;
        const { tab, shiftKey } = isTab(e);

        if (tab && !shiftKey) {
            onButtonBlur();
            e.preventDefault();
        }
    };
});